/**
 * EN / ES translations for the /about page.
 * Imported and merged into the main translations object.
 */
export const aboutTranslations = {
  en: {
    about: {
      eyebrow: "About Kranbos",
      title: "Senior engineering behind every custom AI build",
      subtitle:
        "Kranbos is a small team led by a software engineer who has spent 14+ years building platforms and internal tools for large companies. We apply that same discipline to the quotes, reports and routine admin small and mid-sized businesses handle every day.",
      primaryCta: "Book a free 15-min call",
      secondaryCta: "Contact Us",

      founderEyebrow: "Founder",
      founderName: "Giuliano Kranevitter",
      founderRole: "Founder & Principal Engineer — Miami, FL",
      founderBio1:
        "I have spent my career building developer platforms, internal tooling, and high-traffic web applications — currently as a senior engineer at a Fortune 100 financial services company, where I build and maintain an internal React and Node.js framework that powers its customer-facing digital products.",
      founderBio2:
        "Kranbos exists because the same problems show up in smaller businesses, only more expensive: quotes rebuilt by hand, reports copied between spreadsheets, and answers buried in files. Those are engineering problems, and they are fixable.",
      founderBio3:
        "I lead every project myself, with trusted freelance specialists joining when a project needs them. You are not handed off to someone else after the sale.",
      founderPhotoAlt: "Giuliano Kranevitter, founder of Kranbos",

      statsTitle: "Background in numbers",
      stat1Value: "14+",
      stat1Label: "Years building software professionally",
      stat2Value: "30+",
      stat2Label: "Markets served by a component library we led",
      stat3Value: "22%",
      stat3Label: "Throughput gain from a framework performance rebuild",
      stat4Value: "2",
      stat4Label: "Languages: English and Spanish, natively bilingual",

      experienceTitle: "Where that experience comes from",
      experienceText1:
        "Most of that career has been spent inside Fortune 100 companies, national brands, and global digital agencies — building internal developer platforms, high-traffic websites, real-time search over hundreds of millions of records, and booking and payment experiences used by millions of customers.",
      experienceText2:
        "It also means production systems, performance work, code reviews, and on-call responsibility when things break. That is a very different background from a marketing agency that learned to automate, and it is why the systems we build are made to keep running without being babysat.",
      experienceLogosLabel: "Companies and brands worked with",
      logoAltAmex: "American Express",
      logoAltCondeNast: "Condé Nast",
      logoAltSapientNitro: "SapientNitro",
      logoAltMetrostudio: "Metrostudio",

      principlesTitle: "How we work",
      principle1Title: "We work around your tools",
      principle1Text:
        "No CRM migration, no new software for your team to learn. We build around the tools you already use.",
      principle2Title: "Tested before go-live",
      principle2Text:
        "We test on your own past quotes or reports and show you the match rate before anything goes live.",
      principle3Title: "We own the setup",
      principle3Text:
        "Setup and testing are on us. You stay focused on your work.",
      principle4Title: "Straight answers",
      principle4Text:
        "If custom AI is not the right fix for your problem, we will tell you that on the first call.",

      whoTitle: "Who we work with",
      whoText:
        "Small and mid-sized businesses in the US whose teams quote, report and answer the same questions all day. We work in English and Spanish.",

      ctaTitle: "Find out what custom AI can take off your plate.",
      ctaText:
        "Book a free 15-minute call. We'll look at your current software and show you where manual work could be reduced, whether or not you work with us.",
    },
  },
  es: {
    about: {
      eyebrow: "Sobre Kranbos",
      title: "Ingeniería senior detrás de cada desarrollo de IA a medida",
      subtitle:
        "Kranbos es un equipo pequeño liderado por un ingeniero de software con más de 14 años construyendo plataformas y herramientas internas para grandes empresas. Aplicamos esa misma disciplina a las cotizaciones, reportes y tareas administrativas que los negocios pequeños y medianos manejan cada día.",
      primaryCta: "Agenda una llamada gratis de 15 min",
      secondaryCta: "Contáctanos",

      founderEyebrow: "Fundador",
      founderName: "Giuliano Kranevitter",
      founderRole: "Fundador e Ingeniero Principal — Miami, FL",
      founderBio1:
        "He dedicado mi carrera a construir plataformas para desarrolladores, herramientas internas y aplicaciones web de alto tráfico — actualmente como ingeniero senior en una empresa Fortune 100 de servicios financieros, donde construyo y mantengo un framework interno de React y Node.js que impulsa sus productos digitales de cara al cliente.",
      founderBio2:
        "Kranbos existe porque los mismos problemas aparecen en los negocios más pequeños, solo que más caros: cotizaciones rehechas a mano, reportes copiados entre hojas de cálculo y respuestas enterradas en archivos. Son problemas de ingeniería, y se pueden resolver.",
      founderBio3:
        "Lidero cada proyecto personalmente, con especialistas freelance de confianza que se suman cuando el proyecto lo necesita. No te pasan a otra persona después de la venta.",
      founderPhotoAlt: "Giuliano Kranevitter, fundador de Kranbos",

      statsTitle: "La trayectoria en números",
      stat1Value: "14+",
      stat1Label: "Años construyendo software profesionalmente",
      stat2Value: "30+",
      stat2Label: "Mercados atendidos por una librería de componentes que lideramos",
      stat3Value: "22%",
      stat3Label: "Mejora de throughput en una reconstrucción de performance",
      stat4Value: "2",
      stat4Label: "Idiomas: inglés y español, bilingüe nativo",

      experienceTitle: "De dónde viene esa experiencia",
      experienceText1:
        "Gran parte de esa carrera fue dentro de empresas Fortune 100, marcas nacionales y agencias digitales globales — construyendo plataformas internas para desarrolladores, sitios de alto tráfico, búsqueda en tiempo real sobre cientos de millones de registros y experiencias de reserva y pago usadas por millones de clientes.",
      experienceText2:
        "También significa sistemas en producción, trabajo de performance, revisiones de código y guardias on-call cuando algo se rompe. Es un background muy distinto al de una agencia de marketing que aprendió a automatizar, y por eso los sistemas que construimos están hechos para seguir funcionando sin que haya que cuidarlos.",
      experienceLogosLabel: "Empresas y marcas con las que trabajamos",
      logoAltAmex: "American Express",
      logoAltCondeNast: "Condé Nast",
      logoAltSapientNitro: "SapientNitro",
      logoAltMetrostudio: "Metrostudio",

      principlesTitle: "Cómo trabajamos",
      principle1Title: "Nos adaptamos a tus herramientas",
      principle1Text:
        "Sin migrar el CRM ni software nuevo que tu equipo tenga que aprender. Construimos alrededor de las herramientas que ya usas.",
      principle2Title: "Probado antes de lanzar",
      principle2Text:
        "Probamos con tus propias cotizaciones o reportes anteriores y te mostramos el porcentaje de coincidencia antes de lanzar.",
      principle3Title: "Nos hacemos cargo del setup",
      principle3Text:
        "La configuración y las pruebas corren por nuestra cuenta. Tú te enfocas en tu trabajo.",
      principle4Title: "Respuestas directas",
      principle4Text:
        "Si la IA a medida no es la solución para tu problema, te lo decimos en la primera llamada.",

      whoTitle: "Con quién trabajamos",
      whoText:
        "Negocios pequeños y medianos en Estados Unidos cuyos equipos cotizan, reportan y responden las mismas preguntas todo el día. Trabajamos en inglés y español.",

      ctaTitle: "Descubre qué tareas puede quitarte de encima la IA a medida.",
      ctaText:
        "Agenda una llamada gratis de 15 minutos. Revisaremos tu software actual y te mostraremos dónde se podría reducir el trabajo manual, trabajes con nosotros o no.",
    },
  },
};
